import { getConfig } from "Config";
import axios from "axios";
import { useUserInfo } from "GlobalStates/User";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

const { baseURL } = getConfig();

const apiClient = axios.create({
  baseURL: baseURL, // Replace with your API base URL
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Include cookies with requests
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

export const useAxiosSetup = () => {
  const { user, setUser } = useUserInfo();
  const navigate = useNavigate();
  useEffect(() => {
    // Request Interceptor
    const requestInterceptor = apiClient.interceptors.request.use(
      (config) => {
        if (user?.token) {
          config.headers["Authorization"] = `Bearer ${user?.token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response Interceptor
    const responseInterceptor = apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers["Authorization"] = `Bearer ${token}`;
                return apiClient(originalRequest);
              })
              .catch((err) => Promise.reject(err));
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            const { data } = await apiClient.get("/auth/refreshAccessToken", {
              withCredentials: true, // Ensure cookies are sent
            });

            setUser((prev) => ({ ...prev, token: data.accessToken })); // Update Recoil state with the new access token
            processQueue(null, data.accessToken);
            isRefreshing = false;

            // Retry the failed request with the new token
            originalRequest.headers[
              "Authorization"
            ] = `Bearer ${data.accessToken}`;
            return apiClient(originalRequest);
          } catch (refreshError) {
            processQueue(refreshError, null);
            isRefreshing = false;
            // Navigate to /login if refresh fails
            navigate("/", { replace: true });
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    // Cleanup interceptors on unmount
    return () => {
      apiClient.interceptors.request.eject(requestInterceptor);
      apiClient.interceptors.response.eject(responseInterceptor);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, setUser]);
};

export default apiClient;
